import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Video, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookMeetingModal = ({ open, onOpenChange, lead, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    meeting_datetime: '',
    duration: 30,
    decision_maker_name: lead?.decision_maker_name || '',
    decision_maker_email: lead?.decision_maker_email || '',
    decision_maker_role: lead?.decision_maker_role || '',
    decision_maker_phone: lead?.decision_maker_phone || '',
    business_type: lead?.business_type || '',
    meeting_notes: '',
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.meeting_datetime) {
      toast.error('Please select a meeting date and time');
      return;
    }
    if (!form.decision_maker_name.trim()) {
      toast.error('Please enter the decision maker name');
      return;
    }
    if (!form.decision_maker_email.trim()) {
      toast.error('Please enter the decision maker email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/meetings/book`, {
        lead_id: lead.id,
        meeting_datetime: form.meeting_datetime,
        duration: parseInt(form.duration) || 30,
        decision_maker_name: form.decision_maker_name.trim(),
        decision_maker_email: form.decision_maker_email.trim(),
        decision_maker_role: form.decision_maker_role.trim() || null,
        decision_maker_phone: form.decision_maker_phone.trim() || null,
        business_type: form.business_type.trim() || null,
        meeting_notes: form.meeting_notes.trim() || null,
      });

      const data = response.data;
      if (data.success) {
        toast.success(data.message);
        onOpenChange(false);
        onSuccess?.();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to book meeting';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="book-meeting-modal" aria-describedby="book-meeting-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-blue-400" />
            Book Meeting + Zoom
          </DialogTitle>
          <p id="book-meeting-description" className="text-sm text-muted-foreground mt-1">
            {lead?.full_name} — {lead?.company_name}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Date & Time *</Label>
              <Input
                type="datetime-local"
                value={form.meeting_datetime}
                onChange={(e) => handleChange('meeting_datetime', e.target.value)}
                className="bg-secondary border-border text-sm"
                required
                data-testid="meeting-datetime-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Duration (min)</Label>
              <Input
                type="number"
                min={15}
                max={120}
                value={form.duration}
                onChange={(e) => handleChange('duration', e.target.value)}
                className="bg-secondary border-border text-sm"
                data-testid="meeting-duration-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Decision Maker Name *</Label>
              <Input
                value={form.decision_maker_name}
                onChange={(e) => handleChange('decision_maker_name', e.target.value)}
                placeholder="Full name"
                className="bg-secondary border-border text-sm"
                required
                data-testid="dm-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">DM Email *</Label>
              <Input
                type="email"
                value={form.decision_maker_email}
                onChange={(e) => handleChange('decision_maker_email', e.target.value)}
                placeholder="email@company.com"
                className="bg-secondary border-border text-sm"
                required
                data-testid="dm-email-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Role</Label>
              <Input
                value={form.decision_maker_role}
                onChange={(e) => handleChange('decision_maker_role', e.target.value)}
                placeholder="Owner, Manager..."
                className="bg-secondary border-border text-sm"
                data-testid="dm-role-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">DM Phone</Label>
              <Input
                value={form.decision_maker_phone}
                onChange={(e) => handleChange('decision_maker_phone', e.target.value)}
                placeholder="Direct phone"
                className="bg-secondary border-border text-sm"
                data-testid="dm-phone-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Business Type</Label>
            <Input
              value={form.business_type}
              onChange={(e) => handleChange('business_type', e.target.value)}
              placeholder="Type of business"
              className="bg-secondary border-border text-sm"
              data-testid="business-type-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Meeting Notes</Label>
            <Textarea
              value={form.meeting_notes}
              onChange={(e) => handleChange('meeting_notes', e.target.value)}
              placeholder="Any notes for this meeting..."
              className="bg-secondary border-border text-sm min-h-[60px]"
              data-testid="meeting-notes-input"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-border"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="book-meeting-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Booking...
                </>
              ) : (
                <>
                  <Video className="w-4 h-4 mr-2" />
                  Book Meeting
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookMeetingModal;
